import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { connect } from 'react-redux';
import { googleLogin } from '../actions/auth';
import {
  GOOGLE_CALLBACK_PATH,
  GOOGLE_OAUTH_STATE_KEY,
  getGoogleRedirectUri,
} from '../utils/googleAuth';
import '../styles/sign-in.css';

/**
 * Handles return from Google OAuth redirect (popup-blocker fallback).
 * Expects ?code=...&state=... (or ?error=...).
 */
function GoogleCallback({ googleLogin, isAuthenticated }) {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [error, setError] = useState(null);
  const startedRef = useRef(false);

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/chat', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    if (startedRef.current) return undefined;
    startedRef.current = true;

    const oauthError = searchParams.get('error');
    const code = searchParams.get('code');
    const state = searchParams.get('state');
    const savedState = sessionStorage.getItem(GOOGLE_OAUTH_STATE_KEY);
    sessionStorage.removeItem(GOOGLE_OAUTH_STATE_KEY);

    if (oauthError) {
      setError(
        oauthError === 'access_denied'
          ? 'Sign-in was cancelled.'
          : 'Google Sign-In failed. Please try again.'
      );
      return undefined;
    }

    if (!code) {
      setError('Missing Google authorization code. Please try signing in again.');
      return undefined;
    }

    if (!savedState || !state || savedState !== state) {
      setError('Sign-in session expired or was invalid. Please try again.');
      return undefined;
    }

    let cancelled = false;

    (async () => {
      const result = await googleLogin({
        code,
        redirect_uri: getGoogleRedirectUri(),
      });
      if (cancelled) return;
      if (result === 'success') {
        navigate('/chat', { replace: true });
        return;
      }
      setError(
        typeof result === 'string' && result !== 'error'
          ? result
          : 'Sign in failed.'
      );
    })();

    return () => {
      cancelled = true;
    };
  }, [googleLogin, navigate, searchParams]);

  return (
    <div className="signin-modal" style={{ position: 'fixed' }}>
      <div className="signin-modal__panel" role="status" aria-live="polite">
        <h1 className="signin-modal__title">
          {error ? 'Sign-in issue' : 'Signing you in'}
        </h1>
        <p className="signin-modal__subtitle">
          {error || 'Finishing Google sign-in…'}
        </p>
        {error && (
          <button
            type="button"
            className="google-continue"
            onClick={() => navigate('/', { replace: true, state: { signIn: true } })}
          >
            <span className="google-continue__label">Back to sign in</span>
          </button>
        )}
      </div>
    </div>
  );
}

// Keep path export stable for docs / backend allow-list notes.
GoogleCallback.path = GOOGLE_CALLBACK_PATH;

const mapStateToProps = (state) => ({
  isAuthenticated: state.auth.isAuthenticated,
});

export default connect(mapStateToProps, { googleLogin })(GoogleCallback);
